import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "einsatzmittelvorschlaege";

export type Einsatzmittelvorschlag = {
  readonly id: string;
  readonly name: string;
  readonly funkrufname?: string;
  readonly organisation?: string;
  readonly geteilt: boolean;
  readonly geteiltVon?: { readonly id: string; readonly name: string };
  readonly geteiltVonOrganisation?: string;
};

function useEinsatzmittelvorschlaegeSyncModule(): SyncModule<
  ReadonlyArray<Einsatzmittelvorschlag>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Einsatzmittelvorschläge are a new-Datenportal-only resource — the
      // legacy portal has no equivalent endpoint, so we simply don't fetch
      // anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchEinsatzmittelvorschlaegeNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchEinsatzmittelvorschlaegeNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Einsatzmittelvorschlag>> {
  const response = await fetch(`${baseUrl}/api/device/sync/resources`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.resources ?? []).map(parseEinsatzmittelvorschlagNew);
}

function parseEinsatzmittelvorschlagNew(entry: any): Einsatzmittelvorschlag {
  return {
    id: entry.id,
    name: entry.name,
    funkrufname: entry.radioCallSign || undefined,
    organisation: entry.organisation || undefined,
    geteilt: entry.shared ?? false,
    geteiltVon: entry.sharedBy
      ? { id: entry.sharedBy.id, name: entry.sharedBy.name }
      : undefined,
    geteiltVonOrganisation: entry.sharedFrom || undefined,
  };
}

export function useEinsatzmittelvorschlaegeSync() {
  const module = useEinsatzmittelvorschlaegeSyncModule();
  useSyncModuleRegistration(module);
}

export function useEinsatzmittelvorschlaege(): ReadonlyArray<Einsatzmittelvorschlag> {
  return useSyncData(syncModuleId) ?? [];
}
